import { Bike, DownloadCloud, Infinity, Loader2, Package, Play, Truck } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { xupinhadorService } from "../../services/xupinhador-service";

interface JobState {
  loading: boolean;
  status: "idle" | "success" | "error";
  message: string;
}

const makeState = (): JobState => ({ loading: false, status: "idle", message: "" });

interface JobCardProps {
  title: string;
  description: string;
  icon: React.ReactElement;
  accentClass: string;
  buttonClass: string;
  state: JobState;
  onPlay: () => void;
  tooltip?: string;
}

function StatusBadge({ state }: { state: JobState }) {
  if (state.loading) {
    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Executando...
      </Badge>
    );
  }
  if (!state.message) {
    return <Badge variant="outline">Aguardando</Badge>;
  }
  if (state.status === "success") {
    return <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100 whitespace-normal h-auto py-1">{state.message}</Badge>;
  }
  return <Badge variant="destructive" className="whitespace-normal h-auto py-1">{state.message}</Badge>;
}

function JobCard({ title, description, icon, accentClass, buttonClass, state, onPlay, tooltip }: JobCardProps) {
  return (
    <Card className={`h-full border-t-4 ${accentClass} transition-all hover:-translate-y-1 hover:shadow-md`}>
      <CardContent className="flex flex-col gap-3 p-5 h-full box-border">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-base">{title}</h3>
        </div>
        <p className="text-sm text-muted-foreground flex-1">{description}</p>
        <StatusBadge state={state} />
        <Button
          onClick={onPlay}
          disabled={state.loading}
          title={tooltip}
          className={`w-full font-bold ${buttonClass}`}
        >
          {state.loading
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Executando...</>
            : <><Play className="h-4 w-4 mr-2" />Executar</>
          }
        </Button>
      </CardContent>
    </Card>
  );
}

export default function XupinhadorPage() {
  const [entregoState, setEntregoState] = useState(makeState());
  const [imileState, setImileState] = useState(makeState());
  const [muveState, setMuveState] = useState(makeState());
  const [todosState, setTodosState] = useState(makeState());

  const handlePlay = async (
    fn: (starters: string[]) => Promise<any>,
    starters: string[],
    setState: React.Dispatch<React.SetStateAction<JobState>>
  ) => {
    setState({ loading: true, status: "idle", message: "" });
    try {
      const res = await fn(starters);
      const msg = res.data?.message || "Disparo realizado com sucesso";
      setState({ loading: false, status: "success", message: msg });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao executar o job";
      setState({ loading: false, status: "error", message: msg });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <DownloadCloud className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Downloads Retriever</h1>
          <p className="text-muted-foreground">Disparo manual dos jobs de download</p>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-xl font-bold text-primary">Gestor Play</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <JobCard
            title="Entrego"
            description="Baixa os dados de documentos da operação Entrego."
            icon={<Truck className="h-5 w-5 text-blue-500" />}
            accentClass="border-t-blue-500"
            buttonClass="bg-blue-500 hover:bg-blue-600 text-white"
            state={entregoState}
            onPlay={() => handlePlay(xupinhadorService.gestorPlay, ["entrego"], setEntregoState)}
            tooltip="Dispara o job de download para a operação Entrego"
          />
          <JobCard
            title="iMile"
            description="Baixa as leituras do portal DS da iMile e o financeiro da Realogística (dia anterior)."
            icon={<Package className="h-5 w-5 text-violet-500" />}
            accentClass="border-t-violet-500"
            buttonClass="bg-violet-500 hover:bg-violet-600 text-white"
            state={imileState}
            onPlay={() => handlePlay(xupinhadorService.gestorPlay, ["imile_ds", "imile_realogistica"], setImileState)}
            tooltip="Dispara os jobs de download da iMile (leituras e financeiro)"
          />
          <JobCard
            title="MUVE"
            description="Baixa o garantido e o roteirizador da API da MUVE (dia anterior). Se o dia já subiu pelo portal, não baixa de novo."
            icon={<Bike className="h-5 w-5 text-teal-500" />}
            accentClass="border-t-teal-500"
            buttonClass="bg-teal-500 hover:bg-teal-600 text-white"
            state={muveState}
            onPlay={() => handlePlay(xupinhadorService.gestorPlay, ["muve"], setMuveState)}
            tooltip="Dispara o job de download da MUVE (garantido e roteirizador)"
          />
          <JobCard
            title="Todos"
            description="Executa todos os jobs de download disponíveis em sequência."
            icon={<Infinity className="h-5 w-5 text-red-500" />}
            accentClass="border-t-red-500"
            buttonClass="bg-red-500 hover:bg-red-600 text-white"
            state={todosState}
            onPlay={() => handlePlay(xupinhadorService.gestorPlay, [], setTodosState)}
            tooltip="Dispara todos os jobs de download"
          />
        </div>
      </div>
    </div>
  );
}
